<script setup lang="ts">
// A storage's detail (DetailPanel): what the node's checks found and a retry.
import { computed } from 'vue'
import { api, type Storage, type StorageStatus } from '../../api'
import { useNode } from '../../store'
import { ago, gbText } from '../../util'

defineProps<{ storage: Storage; storageSt?: StorageStatus }>()
const node = useNode()
const now = computed(() => node.now)
const gb = gbText
async function remount() {
    try {
        await api.storageRetry()
        node.notify('Mounting again…')
    } catch (e) {
        node.notify(e instanceof Error ? e.message : String(e), true)
    }
}
</script>

<template>
    <div class="text-xs text-fg-2">
        {{ storage.kind === 'disk' ? 'Drive' : storage.kind.toUpperCase() }} ·
        <span class="font-mono tabular-nums">{{
            storage.kind === 'disk'
                ? `${storage.fstype} ${storage.uuid}`
                : storage.kind === 'smb'
                  ? `//${storage.server}/${storage.share}`
                  : `${storage.server}:${storage.share}`
        }}</span>
        → <span class="font-mono tabular-nums">/mnt/ese/{{ storage.id }}</span
        ><template v-if="storage.kind === 'smb'">
            · {{ storage.username ? 'as ' + storage.username : 'guest' }}</template
        ><template v-if="storage.kind === 'disk'"> · mounted whenever it is connected</template>
    </div>
    <div class="mt-2.5 grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-x-3.5 gap-y-2.5">
        <div>
            <div class="text-[11px] text-fg-3">state</div>
            <div class="text-[13px]">
                {{ (storageSt?.state ?? 'starting').replace('_', ' ') }}
                <span v-if="storageSt" class="text-fg-2">{{ ago(storageSt.since, now) }}</span>
            </div>
        </div>
        <div>
            <div class="text-[11px] text-fg-3">mounted</div>
            <div class="text-[13px]">{{ storageSt?.mounted ? 'yes' : 'no' }}</div>
        </div>
        <div>
            <div class="text-[11px] text-fg-3">writable</div>
            <div class="text-[13px]">{{ storageSt?.writable ? 'yes' : 'no' }}</div>
        </div>
        <div v-if="storageSt?.mounted">
            <div class="text-[11px] text-fg-3">free</div>
            <div class="font-mono text-[13px] tabular-nums">
                {{ gb(storageSt.free_gb) }}
                <span class="text-fg-2">/ {{ gb(storageSt.total_gb) }}</span>
            </div>
        </div>
    </div>
    <div v-if="storageSt?.error" class="mt-2 text-xs text-bad">{{ storageSt.error }}</div>
    <div class="mt-2.5 flex items-center gap-2">
        <button class="btn-ghost text-xs" @click="remount">mount again</button
        ><span class="text-xs text-fg-3">checked every 15 s: mounted, a test file written and removed, free space</span>
    </div>
</template>
